import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faUniversity, faSpinner, faSearch, faSync,
    faBuilding, faTimes, faEye, faTrash, faExclamationTriangle,
    faBalanceScale,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../Layout';
import HMSBadge from '../ui/HMSBadge';
import HMSButton from '../ui/HMSButton';
import HMSEmptyState from '../ui/HMSEmptyState';
import HMSPageHeader from '../ui/HMSPageHeader';
import HMSSkeleton from '../ui/HMSSkeleton';
import Footer from '../ui/Footer';
import bankReconService from '../../services/bankReconService';
// The CRM endpoint is the documented single source of truth for entities
// (see hmsEntityService). invoicingService's version hits /api/offer/hms-entities/,
// which is gated on HasTradeOpsAccess -- so a financial-access-only user, the
// exact role bank recon exists for, got a 403, the component swallowed it into an
// empty list, and the screen said "Select an Entity" with no entities to select.
import { fetchHMSEntities } from '../../services/hmsEntityService';
import { lookupNames } from '../../services/documentNamingService';
import BankStatementCaptureModal from './BankStatementCaptureModal';
import BankStatementDetailModal from './BankStatementDetailModal';
import '../../pages/Invoicing/OutgoingInvoice.css';
import './BankReconDashboard.css';

const BankReconDashboard = () => {
    const history = useHistory();
    const [showCaptureModal, setShowCaptureModal] = useState(false);
    const [detailStatement, setDetailStatement] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [statements, setStatements] = useState([]);
    const [stmtDisplayNames, setStmtDisplayNames] = useState({});
    const [entities, setEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        loadEntities();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadEntities = async () => {
        try {
            // The CRM service returns {value, label}; this screen keys on
            // {id, hms_entity_name}. Normalise here rather than teaching the
            // shared service a second shape.
            const data = await fetchHMSEntities();
            const raw = Array.isArray(data) ? data : (data.results || []);
            const list = raw.map(e => ({
                id: e.value ?? e.id,
                hms_entity_name: e.label ?? e.hms_entity_name,
            })).filter(e => e.id != null);
            setEntities(list);
            if (list.length === 1) setSelectedEntity(String(list[0].id));
        } catch (err) {
            console.error('Failed to load entities:', err);
        }
    };

    const loadStatements = useCallback(async () => {
        if (!selectedEntity) { setStatements([]); return; }
        setLoading(true);
        try {
            const response = await bankReconService.getStatements({ hms_entity: selectedEntity });
            const stmts = response.data || [];
            setStatements(stmts);
            
            try {
                const docIds = stmts
                    .map(s => s?.docuware_document_id)
                    .filter(Boolean)
                    .map(String);
                if (docIds.length > 0) {
                    const map = await lookupNames(docIds, 'bank_statement');
                    setStmtDisplayNames(map || {});
                }
            } catch (lookupErr) {
                console.warn('Failed to lookup AI display names for bank statements (non-fatal):', lookupErr);
            }
        } catch (err) {
            console.error('Failed to load statements:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedEntity]);

    useEffect(() => {
        loadStatements();
    }, [loadStatements]);

    const handleCaptureSuccess = () => {
        setShowCaptureModal(false);
        loadStatements();
    };

    const toggleDropdown = (id) => {
        setActiveDropdown(activeDropdown === id ? null : id);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await bankReconService.discardUpload(deleteTarget.id);
            setDeleteTarget(null);
            loadStatements();
        } catch (err) {
            console.error('Failed to delete statement:', err);
        } finally {
            setDeleting(false);
        }
    };

    const handleViewLines = (stmt) => {
        setDetailStatement(stmt);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    const filtered = statements.filter(s => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (s.bank_name || '').toLowerCase().includes(term) ||
            (s.iban || '').toLowerCase().includes(term) ||
            (s.account_number || '').toLowerCase().includes(term) ||
            (s.original_filename || '').toLowerCase().includes(term) ||
            (s.currency || '').toLowerCase().includes(term);
    });

    // Header tab tray: one pill per entity. No counts — the old tab
    // buttons carried none either. The tray only renders once entities load.
    const entityTabs = entities.map(e => ({
        key: String(e.id),
        label: e.hms_entity_name,
        icon: faBuilding,
    }));

    // Switching entity clears the search box, exactly as the tab buttons did.
    const handleEntityChange = (key) => {
        setSelectedEntity(key);
        setSearchTerm('');
    };

    return (
        <Layout showLeftNavSidebar={true} showRightSidebar={false}>
            <div className="invoice-page-container bank-recon-screen">
                {/* Shared screen header: brand block, entity pill tray, upload action */}
                <HMSPageHeader
                    title="Bank Reconciliation"
                    kicker="Finance"
                    tabs={entityTabs.length > 0
                        ? { items: entityTabs, activeKey: selectedEntity, onChange: handleEntityChange, alwaysLabel: true }
                        : undefined}
                    actions={
                        <button
                            className="btn-primary"
                            onClick={() => setShowCaptureModal(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Upload Statement
                        </button>
                    }
                />

                <div className="search-filter-controls">
                    <div className="master-data-search-box">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input
                            type="text"
                            className="master-data-search-input"
                            placeholder={selectedEntity ? "Search by bank, IBAN, account, filename, or currency..." : "Select an entity above..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={!selectedEntity}
                        />
                        {searchTerm && (
                            <button className="clear-search" onClick={() => setSearchTerm('')}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                    <button className="reload-button" onClick={loadStatements} title="Reload statements" disabled={!selectedEntity}>
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                </div>

                <div className="invoice-list-container">
                    {!selectedEntity ? (
                        /* `card` because the block this replaces drew its own white
                           panel around the centred text. */
                        <HMSEmptyState
                            variant="card"
                            icon={<FontAwesomeIcon icon={faUniversity} />}
                            title="Select an Entity"
                            hint="Choose an entity above to view its bank statements"
                        />
                    ) : loading ? (
                        /* Skeleton in the shape of the statement table that is
                           coming. `.loading-state` keeps the padding the spinner
                           block used; only its centring has to go, or the bars
                           collapse to their content width. */
                        <div className="loading-state" style={{ alignItems: 'stretch' }}>
                            <HMSSkeleton shape="title" />
                            <HMSSkeleton lines={6} />
                        </div>
                    ) : filtered.length === 0 ? (
                        <HMSEmptyState
                            variant="card"
                            icon={<FontAwesomeIcon icon={faUniversity} />}
                            title={searchTerm ? 'No Results' : 'No Bank Statements'}
                            hint={searchTerm ? 'No statements match your search' : 'No bank statements found for this entity'}
                        />
                    ) : (
                        <table className="invoice-list-table">
                            <thead>
                                <tr>
                                    <th>Bank</th>
                                    <th>IBAN / Account</th>
                                    <th>Statement Date</th>
                                    <th>Currency</th>
                                    <th>Lines</th>
                                    <th>Filename</th>
                                    <th>Uploaded</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(stmt => (
                                    <tr
                                        key={stmt.id}
                                        onClick={() => handleViewLines(stmt)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="invoice-number-cell">{stmt.bank_name || stmt.bic || '-'}</td>
                                        <td>
                                            <span className="bank-recon-iban">
                                                {stmt.iban || stmt.account_number || '-'}
                                            </span>
                                        </td>
                                        <td>{formatDate(stmt.statement_date)}</td>
                                        <td>{stmt.currency || '-'}</td>
                                        <td>{stmt.total_lines}</td>
                                        <td className="bank-recon-filename" title={stmt.original_filename || ''}>
                                            {(stmt.docuware_document_id && stmtDisplayNames?.[String(stmt.docuware_document_id)]?.display_name) || stmt.original_filename || '-'}
                                        </td>
                                        <td>{formatDate(stmt.created_at)}</td>
                                        <td>
                                            {/* Every uploaded statement lands confirmed, and the tone the
                                                screen already used for it (#d1fae5 on #065f46) is the
                                                primitive's `approved` exactly — only the green ring the
                                                shared .status-badge family adds is dropped. */}
                                            <HMSBadge status="approved">Confirmed</HMSBadge>
                                        </td>
                                        <td className="actions-cell">
                                            <div
                                                className={`action-dropdown ${activeDropdown === stmt.id ? 'dropdown-open' : ''}`}
                                                onClick={(e) => e.stopPropagation()}
                                                ref={activeDropdown === stmt.id ? dropdownRef : null}
                                            >
                                                <button
                                                    className="gear-button"
                                                    onClick={(e) => { e.stopPropagation(); toggleDropdown(stmt.id); }}
                                                >
                                                    Actions
                                                </button>
                                                {activeDropdown === stmt.id && (
                                                    <div className="dropdown-menu">
                                                        <button onClick={() => { setActiveDropdown(null); history.push(`/invoicing/bank-reconciliation/reconcile/${stmt.id}`); }}>
                                                            <FontAwesomeIcon icon={faBalanceScale} /> Reconcile
                                                        </button>
                                                        <button onClick={() => { setActiveDropdown(null); handleViewLines(stmt); }}>
                                                            <FontAwesomeIcon icon={faEye} /> View Lines
                                                        </button>
                                                        <hr />
                                                        <button className="danger" onClick={() => { setActiveDropdown(null); setDeleteTarget(stmt); }}>
                                                            <FontAwesomeIcon icon={faTrash} /> Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <Footer />
            </div>

            <BankStatementCaptureModal
                isOpen={showCaptureModal}
                onClose={() => setShowCaptureModal(false)}
                onSuccess={handleCaptureSuccess}
            />

            {detailStatement && (
                <BankStatementDetailModal
                    statement={detailStatement}
                    onClose={() => setDetailStatement(null)}
                />
            )}

            {deleteTarget && (
                <div className="md-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
                    <div className="md-modal bank-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="md-modal-header bank-delete-modal-header">
                            <div className="md-modal-header-text">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="md-modal-header-icon" />
                                <h2>Delete Statement</h2>
                            </div>
                            <button className="md-modal-close" onClick={() => !deleting && setDeleteTarget(null)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="md-modal-body">
                            <div className="bank-delete-body">
                                <div className="bank-delete-icon-wrap">
                                    <FontAwesomeIcon icon={faTrash} />
                                </div>
                                <p className="bank-delete-message">
                                    Are you sure you want to permanently delete this bank statement?
                                </p>
                                <div className="bank-delete-details">
                                    <div className="bank-delete-detail-row">
                                        <span className="bank-delete-detail-label">File</span>
                                        <span className="bank-delete-detail-value">{deleteTarget.original_filename}</span>
                                    </div>
                                    {deleteTarget.bank_name && (
                                        <div className="bank-delete-detail-row">
                                            <span className="bank-delete-detail-label">Bank</span>
                                            <span className="bank-delete-detail-value">{deleteTarget.bank_name}</span>
                                        </div>
                                    )}
                                    {deleteTarget.iban && (
                                        <div className="bank-delete-detail-row">
                                            <span className="bank-delete-detail-label">IBAN</span>
                                            <span className="bank-delete-detail-value bank-recon-iban">{deleteTarget.iban}</span>
                                        </div>
                                    )}
                                    {deleteTarget.total_lines > 0 && (
                                        <div className="bank-delete-detail-row">
                                            <span className="bank-delete-detail-label">Lines</span>
                                            <span className="bank-delete-detail-value">{deleteTarget.total_lines} transaction(s)</span>
                                        </div>
                                    )}
                                </div>
                                <p className="bank-delete-warning">
                                    This will remove the statement, all transaction lines, and the document from DocuWare. This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-btn-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                                Cancel
                            </button>
                            <HMSButton variant="danger" onClick={confirmDelete} disabled={deleting}>
                                {deleting ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Deleting...</>
                                ) : (
                                    <><FontAwesomeIcon icon={faTrash} /> Delete Permanently</>
                                )}
                            </HMSButton>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default BankReconDashboard;
